import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const INVESTOR_TYPES = [
  "Institutional / Fund",
  "Family Office",
  "Strategic Investor",
  "DFI / Multilateral",
  "High Net Worth Individual",
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    organization: "",
    investor_type: "",
    notes: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await register(form);
    setLoading(false);
    if (res.ok) setSuccess(true);
    else setError(res.error);
  };

  if (success) {
    return (
      <div data-testid="register-success" className="min-h-screen bg-[#F9F9F6] flex items-center justify-center px-6">
        <div className="max-w-xl text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 border border-[#5C2018] mb-8">
            <Check className="text-[#5C2018]" />
          </div>
          <p className="eyebrow text-[#5C2018]">Submitted</p>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight mt-5">
            Your access request is with LRG Holdings.
          </h1>
          <p className="mt-6 text-base text-[#1A1A1A] leading-relaxed">
            Our team reviews investor access manually to ensure institutional-grade conversations. You will receive an email at{" "}
            <span className="font-medium">{form.email}</span> when your account is approved.
          </p>
          <Link
            to="/"
            data-testid="register-back-home"
            className="mt-10 inline-flex items-center gap-3 px-7 py-4 bg-[#5C2018] text-[#F9F9F6] text-[12px] uppercase tracking-[0.2em] font-semibold hover:bg-[#1A1A1A] transition-colors"
          >
            Return to site
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="page-register" className="min-h-screen bg-[#F9F9F6] flex items-center justify-center p-6 md:p-12">
      <div className="w-full max-w-2xl">
        <Link to="/" className="inline-flex items-center gap-2 mb-8 text-[12px] uppercase tracking-[0.18em] text-[#666666]">
          ← Back to site
        </Link>
        <p className="eyebrow">Request access</p>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight mt-4">
          Apply for the investor portal.
        </h1>
        <p className="mt-3 text-sm text-[#666666] max-w-xl">
          Provide a few details and the LRG team will review and approve your access. You will receive a confirmation email once
          your account is live.
        </p>

        <form onSubmit={onSubmit} className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" data-testid="register-form">
          <Field label="Full name" required>
            <input
              type="text"
              required
              value={form.name}
              onChange={change("name")}
              data-testid="register-name-input"
              className="mt-2 w-full bg-transparent border-b border-[#1A1A1A] py-3 text-base focus:outline-none focus:border-[#5C2018]"
            />
          </Field>
          <Field label="Email" required>
            <input
              type="email"
              required
              value={form.email}
              onChange={change("email")}
              data-testid="register-email-input"
              className="mt-2 w-full bg-transparent border-b border-[#1A1A1A] py-3 text-base focus:outline-none focus:border-[#5C2018]"
            />
          </Field>
          <Field label="Password" required>
            <input
              type="password"
              required
              minLength={8}
              value={form.password}
              onChange={change("password")}
              data-testid="register-password-input"
              className="mt-2 w-full bg-transparent border-b border-[#1A1A1A] py-3 text-base focus:outline-none focus:border-[#5C2018]"
              placeholder="At least 8 characters"
            />
          </Field>
          <Field label="Organization">
            <input
              type="text"
              value={form.organization}
              onChange={change("organization")}
              data-testid="register-org-input"
              className="mt-2 w-full bg-transparent border-b border-[#1A1A1A] py-3 text-base focus:outline-none focus:border-[#5C2018]"
            />
          </Field>
          <Field label="Investor type" className="md:col-span-2">
            <select
              value={form.investor_type}
              onChange={change("investor_type")}
              data-testid="register-type-select"
              className="mt-2 w-full bg-transparent border-b border-[#1A1A1A] py-3 text-base focus:outline-none focus:border-[#5C2018]"
            >
              <option value="">Select…</option>
              {INVESTOR_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Notes / context" className="md:col-span-2">
            <textarea
              rows={4}
              value={form.notes}
              onChange={change("notes")}
              data-testid="register-notes-input"
              className="mt-2 w-full bg-transparent border border-[#E5E5E0] p-3 text-base focus:outline-none focus:border-[#5C2018]"
              placeholder="Capital range, time horizon, areas of interest…"
            />
          </Field>

          {error && (
            <p data-testid="register-error" className="md:col-span-2 text-sm text-[#5C2018] border-l-2 border-[#5C2018] pl-3 py-2 bg-[#5C2018]/5">
              {error}
            </p>
          )}

          <div className="md:col-span-2 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between mt-2">
            <p className="text-xs text-[#666666] max-w-xs">
              Submitting creates a pending account. LRG admin will approve you before login.
            </p>
            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit"
              className="inline-flex items-center justify-center gap-3 px-7 py-4 bg-[#5C2018] text-[#F9F9F6] text-[12px] uppercase tracking-[0.2em] font-semibold hover:bg-[#1A1A1A] transition-colors disabled:opacity-60"
            >
              {loading ? "Submitting…" : "Request access"}
              <ArrowRight size={16} />
            </button>
          </div>
        </form>

        <p className="mt-10 text-sm text-[#666666]">
          Already approved?{" "}
          <Link to="/login" data-testid="register-to-login" className="text-[#1A1A1A] border-b border-[#1A1A1A] pb-0.5 hover:text-[#5C2018] hover:border-[#5C2018]">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

function Field({ label, children, required, className = "" }) {
  return (
    <div className={className}>
      <label className="eyebrow">
        {label}
        {required && <span className="text-[#5C2018] ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}
